import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Filter, X, MapPin, Briefcase } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import Layout from '../components/layout/Layout';
import { useLanguage, technologies, skillMissionMap } from '../i18n';

const MissionsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, getMissions, getEmployers, getMissionDetails, getSectors, getCountries, getSkills } = useLanguage();
  
  const missions = getMissions();
  const employers = getEmployers();
  const missionDetails = getMissionDetails();
  const sectors = getSectors();
  const countries = getCountries();
  const skills = getSkills();

  const [filters, setFilters] = useState({
    technology: 'all',
    sector: 'all',
    country: 'all',
    employer: 'all',
    skill: 'all',
  });
  const [selectedMission, setSelectedMission] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Handle skill filter from URL params
  useEffect(() => {
    const skillParam = searchParams.get('skill');
    if (skillParam) {
      setFilters(prev => ({ ...prev, skill: skillParam }));
    }
  }, [searchParams]);

  const filteredMissions = useMemo(() => {
    return missions.filter((mission) => {
      if (filters.technology !== 'all' && !mission.techs.includes(filters.technology)) return false;
      if (filters.sector !== 'all' && mission.sector !== filters.sector) return false;
      if (filters.country !== 'all' && mission.country !== filters.country) return false;
      if (filters.employer !== 'all' && mission.employer !== filters.employer) return false;
      if (filters.skill !== 'all') {
        const skillMissions = skillMissionMap[filters.skill] || [];
        if (!skillMissions.includes(mission.id)) return false;
      }
      return true;
    });
  }, [filters, missions]);

  const clearFilters = () => {
    setFilters({ technology: 'all', sector: 'all', country: 'all', employer: 'all', skill: 'all' });
    setSearchParams({});
  };
  
  const hasActiveFilters = Object.values(filters).some(v => v !== 'all');

  const handleMissionClick = (mission) => {
    setSelectedMission(mission);
    setIsDialogOpen(true);
  };

  const getEmployerName = (employerId) => {
    const employer = employers.find(e => e.id === employerId);
    return employer ? employer.name : employerId;
  };

  const getSkillName = (skillId) => {
    const skill = skills.find(s => s.id === skillId);
    return skill ? skill.name : skillId;
  };

  const isInternalMission = (mission) => {
    // Check if client explicitly contains "interne" or "internal"
    return mission.client.toLowerCase().includes('interne') || 
           mission.client.toLowerCase().includes('internal');
  };

  const getClientEmployerDisplay = (mission) => {
    const employerName = getEmployerName(mission.employer);
    if (isInternalMission(mission)) {
      return `${employerName} (${t('missions.internalMission')})`;
    }
    // If client is the same as employer (like GMF), just show employer name
    if (mission.client === employerName || mission.client.includes(employerName)) {
      return employerName;
    }
    return `${mission.client} — ${employerName}`;
  };

  const getTechArray = (techString) => techString.split(', ');

  return (
    <Layout>
      <section className="py-12 lg:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-12 lg:mb-16">
            <h1 className="section-title">{t('missions.title')}</h1>
            <p className="font-body text-lg text-slate-600 mt-4 max-w-2xl">
              {t('missions.description')}
            </p>
          </div>

          <div className="bg-white border border-slate-200 p-6 mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Filter className="w-4 h-4 text-blue-600" />
              <span className="font-mono text-xs uppercase tracking-wider text-slate-500">{t('missions.filterLabel')}</span>
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="ml-auto font-mono text-xs text-slate-500 hover:text-blue-600 rounded-none">
                  <X className="w-3 h-3 mr-1" />{t('missions.clearAll')}
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Select value={filters.technology} onValueChange={(value) => setFilters(prev => ({ ...prev, technology: value }))}>
                <SelectTrigger className="rounded-none border-slate-200 font-body"><SelectValue placeholder="Technology" /></SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="all">{t('missions.allTechnologies')}</SelectItem>
                  {technologies.map((tech) => (<SelectItem key={tech} value={tech}>{tech}</SelectItem>))}
                </SelectContent>
              </Select>
              <Select value={filters.sector} onValueChange={(value) => setFilters(prev => ({ ...prev, sector: value }))}>
                <SelectTrigger className="rounded-none border-slate-200 font-body"><SelectValue placeholder="Sector" /></SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="all">{t('missions.allSectors')}</SelectItem>
                  {sectors.map((sector) => (<SelectItem key={sector} value={sector}>{sector}</SelectItem>))}
                </SelectContent>
              </Select>
              <Select value={filters.country} onValueChange={(value) => setFilters(prev => ({ ...prev, country: value }))}>
                <SelectTrigger className="rounded-none border-slate-200 font-body"><SelectValue placeholder="Country" /></SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="all">{t('missions.allCountries')}</SelectItem>
                  {countries.map((country) => (<SelectItem key={country} value={country}>{country}</SelectItem>))}
                </SelectContent>
              </Select>
              <Select value={filters.employer} onValueChange={(value) => setFilters(prev => ({ ...prev, employer: value }))}>
                <SelectTrigger className="rounded-none border-slate-200 font-body"><SelectValue placeholder="Employer" /></SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="all">{t('missions.allEmployers')}</SelectItem>
                  {employers.map((employer) => (<SelectItem key={employer.id} value={employer.id}>{employer.name}</SelectItem>))}
                </SelectContent>
              </Select>
              <Select value={filters.skill} onValueChange={(value) => { setFilters(prev => ({ ...prev, skill: value })); if (value === 'all') setSearchParams({}); }}>
                <SelectTrigger className="rounded-none border-slate-200 font-body"><SelectValue placeholder={t('missions.allSkills')} /></SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="all">{t('missions.allSkills')}</SelectItem>
                  {skills.map((skill) => (<SelectItem key={skill.id} value={skill.id}>{skill.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            {filters.skill !== 'all' && (
              <div className="mt-4 flex items-center gap-2">
                <span className="font-mono text-xs text-slate-500">{t('missions.filteredBySkill')}:</span>
                <Badge className="font-mono text-xs rounded-none bg-blue-100 text-blue-700 border border-blue-200">
                  {getSkillName(filters.skill)}
                </Badge>
              </div>
            )}
          </div>

          <div className="mb-6">
            <span className="font-mono text-xs text-slate-500">
              {t('missions.showing')} {filteredMissions.length} {t('missions.of')} {missions.length} {t('missions.missionsLabel')}
            </span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filteredMissions.map((mission) => (
              <button key={mission.id} onClick={() => handleMissionClick(mission)} className="group text-left bg-white border border-slate-200 p-6 lg:p-8 hover:border-blue-600 transition-all duration-200">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <span className="font-mono text-xs text-blue-600 uppercase tracking-wider">{mission.sector}</span>
                    <h3 className="font-display text-xl font-medium text-slate-800 mt-2">{mission.title}</h3>
                  </div>
                </div>
                <div className="flex items-center gap-2 mb-4 text-sm text-slate-600">
                  <Briefcase className="w-4 h-4" />
                  <span className="font-body">{getClientEmployerDisplay(mission)}</span>
                </div>
                <p className="font-body text-sm text-slate-600 mb-4 line-clamp-2 text-justify">{mission.context}</p>
                <div className="flex flex-wrap gap-2 pt-4 border-t border-slate-200">
                  {getTechArray(mission.techs).slice(0, 4).map((tech) => (
                    <Badge key={tech} variant="outline" className="font-mono text-xs rounded-none border-slate-200 text-slate-500">{tech}</Badge>
                  ))}
                </div>
                <div className="flex items-center gap-1.5 mt-4 text-xs text-slate-500">
                  <MapPin className="w-3 h-3" /><span className="font-mono">{mission.country}</span>
                </div>
              </button>
            ))}
          </div>

          {filteredMissions.length === 0 && (
            <div className="text-center py-16">
              <p className="font-body text-slate-600 mb-4">{t('missions.noResults')}</p>
              <Button variant="outline" onClick={clearFilters} className="font-mono text-xs uppercase tracking-wider rounded-none">{t('missions.clearFilters')}</Button>
            </div>
          )}

          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto bg-white rounded-none">
              {selectedMission && (
                <>
                  <DialogHeader>
                    <div className="mb-2">
                      <span className="label-mono">{selectedMission.sector}</span>
                    </div>
                    <DialogTitle className="font-display text-2xl font-semibold">{selectedMission.title}</DialogTitle>
                    <DialogDescription className="font-body text-slate-600">
                      {selectedMission.client} • {getEmployerName(selectedMission.employer)}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="mt-6 space-y-6">
                    <div>
                      <h4 className="font-mono text-xs uppercase tracking-wider text-slate-500 mb-3">{t('missions.businessContext')}</h4>
                      <p className="font-body text-slate-800 leading-relaxed text-justify">{selectedMission.context}</p>
                    </div>
                    <div>
                      <h4 className="font-mono text-xs uppercase tracking-wider text-slate-500 mb-3">{t('missions.responsibilities')}</h4>
                      <ul className="space-y-2">
                        {(missionDetails[selectedMission.id] || []).map((resp, index) => (
                          <li key={index} className="flex items-start gap-3">
                            <span className="font-mono text-xs text-blue-600 mt-1.5">{String(index + 1).padStart(2, '0')}</span>
                            <span className="font-body text-slate-800">{resp}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-mono text-xs uppercase tracking-wider text-slate-500 mb-3">{t('missions.technologies')}</h4>
                      <div className="flex flex-wrap gap-2">
                        {getTechArray(selectedMission.techs).map((tech) => (
                          <Badge key={tech} className="font-mono text-xs rounded-none bg-blue-50 text-blue-600 border border-blue-200">{tech}</Badge>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 pt-4 border-t border-slate-200">
                      <MapPin className="w-4 h-4 text-slate-500" />
                      <span className="font-mono text-sm text-slate-500">{selectedMission.country}</span>
                    </div>
                  </div>
                </>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </section>
    </Layout>
  );
};

export default MissionsPage;
